import { DateTime } from "luxon";
import { differenceInDays } from "@/shared/dateFormatUtils";
import { BlockedAvailabilityModel } from "@/store/modules/calendar";
import _ from "lodash";

export type CalendarWindow = {
  w: number;
  h: number;
  l: number;
  t: number;
};

export type CalendarBoxConfigCell = {
  w: number;
  h: number;
  hl: number;
  numberOfReservations?: number;
};

export type CalendarBoxConfig = {
  cell: CalendarBoxConfigCell;
  preload: number;
  centerOffset: number;
};

export type CalendarExpandableRow = {
  id: number;
  name: string;
  expanded: boolean;
  height: number;
  offset: number;
};

export class CalendarPropertyView implements CalendarExpandableRow {
  height: number;
  offset = 0;

  constructor(
    public id: number,
    public name: string,
    public shortName: string,
    public expanded: boolean,
    public unitTypes: CalendarUnitTypeView[],
    public unitTypesToDisplay: CalendarUnitTypeView[],
    c: CalendarBoxConfigCell
  ) {
    this.height = c.h + unitTypes.length * c.h;
  }

  mergeOnRendered(availabilities: CalendarAvailabilityView[], reservations: any[]) {
    for (const ut of this.unitTypes) {
      ut.merge(availabilities.filter(a => a.unitTypeId == ut.id), reservations.filter(r => r.unitTypeId == ut.id));
    }
  }

  mergeBlockedAvailabilities(blockedAvailabilities: BlockedAvailabilityModel[]) {
    for (const ut of this.unitTypes) {
      ut.mergeBlockedAvailabilities(blockedAvailabilities.filter(x => x.unitTypeId === ut.id));
    }
  }

  h(
    propertyCount: number,
    c: CalendarBoxConfigCell,
    isMobile: boolean,
    showUnits: boolean,
    showRatePlans: boolean,
    showOBRules: boolean,
    showInputPrices: boolean,
    showRatePlanFields: string[],
    showListRatePlans: number[],
    highlightPeriod?: string[]
  ) {
    let labelHeight = 0;
    if (propertyCount > 1) {
      if (this.name.length > 56 && this.name.length <= 84) {
        labelHeight = isMobile ? 0 : 15;
      } else if (this.name.length > 84) {
        labelHeight = isMobile
          ? c.w >= 390
            ? 0
            : 6
          : 25;
      }
    }
    return (
      c.h + labelHeight +
      (this.expanded
        ? this.unitTypes.reduce(
          (acc, x) =>
            acc + x.h(c, isMobile, showUnits, showRatePlans, showOBRules, showInputPrices, showRatePlanFields, showListRatePlans, this.getUnitsToDisplay(x.units, highlightPeriod)),
          0
        )
        : 0)
    );
  }

  getUnitsToDisplay(units: CalendarUnitView[], highlightPeriod?: string[]) {
    if (
      !highlightPeriod ||
      highlightPeriod.length === 0
    )
      return units.length;

    const highlightPeriodStart = highlightPeriod[0];
    const highlightPeriodEnd = highlightPeriod[1];

    return units.filter(
      (x) =>
        !x.reservations?.some(
          (r) =>
            r.arrivalDate.substring(0, 10) < highlightPeriodEnd &&
            r.departureDate.substring(0, 10) > highlightPeriodStart
        )
    ).length;
  }
}

export class CalendarUnitTypeView implements CalendarExpandableRow {
  constructor(
    public id: number,
    public name: string,
    public baseBedsCapacity: number,
    public extraBedsCapacity: number,
    public expanded: boolean,
    public height: number,
    public offset: number,
    public ratePlansHeight: number,
    public ratePlans: CalendarRatePlanAccomodationUnitTypeView[],
    public units: CalendarUnitView[],
    public unitsToDisplay: CalendarUnitView[]
  ) { }

  availabilityOnDay(day: DateTime, isOwner?: boolean) {
    const ds = day.toISODate();

    // prettier-ignore
    if (this.ratePlans.length > 0) {
      if (!isOwner) {
        return this.ratePlans.flatMap(x => x.availability).find(a => a.date.substring(0, 10) === ds)?.quantity;
      }
      else {
        return this.ratePlans.flatMap(x => x.availability).find(a => a.date.substring(0, 10) === ds)?.ownerQuantity;
      }
    }
  }

  isBlockedOnDay(day: DateTime) {
    return !this.ratePlans.flatMap(x => x.availabilityOnDay(day)).some(x => x?.isBlocked === false);
  }

  merge(availabilities: CalendarAvailabilityView[], reservations: any[]) {
    for (const rp of this.ratePlans) {
      rp.merge(availabilities.filter((a) => a.ratePlansAccomodationUnitTypeId === rp.id));
    }

    for (const u of this.units) {
      const reservationsForUnit = reservations.filter(
        (x) => x.accomodationUnitId === u.id
      );
      u.reservations = [...reservationsForUnit];
      u.calculateOverbooking();
    }
  }

  mergeBlockedAvailabilities(blockedAvailabilities: BlockedAvailabilityModel[]) {
    for (const rp of this.ratePlans) {
      rp.mergeBlockedAvailabilities(blockedAvailabilities.filter((b) => b.ratePlansAccomodationUnitTypeId === rp.id));
    }
  }

  h(
    c: CalendarBoxConfigCell,
    isMobile: boolean,
    showUnits: boolean,
    showRatePlans: boolean,
    showOBRules: boolean,
    showInputPrices: boolean,
    showRatePlanFields: string[],
    showListRatePlans: number[],
    numberOfAvailableUnits?: number
  ) {
    const unitsHeight = showUnits
      ? this.units.slice(0, numberOfAvailableUnits).reduce((acc, x) => acc + x.height, 0)
      : 0

    const selectedAndExpandedRates = this.ratePlans.filter(x => showListRatePlans.includes(x.ratePlanId) && x.expanded).map(x => x.id);

    const obRatePlansHeight = showRatePlans && showOBRules
      ? !isMobile 
        ? this.ratePlans.filter(x => !x.isDefault && selectedAndExpandedRates.includes(x.parentId ?? 0))
          .reduce(
            (acc, x) => acc + x.h(c, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length)
              + x.hi(c, showInputPrices, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length),
            0
          )
        : this.ratePlans.filter(x => !x.isDefault && selectedAndExpandedRates.includes(x.parentId ?? 0)).length * 32
      : 0;

    const ratePlansHeight = showRatePlans
      ? this.ratePlans
        .filter(x => showListRatePlans.includes(x.ratePlanId) && x.isDefault)
        .reduce(
          (acc, x) => acc + x.h(c, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length)
            + x.hi(c, showInputPrices, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length),
          0
        )
      : 0;

    if (isMobile) {
      return c.h + (this.expanded ? ratePlansHeight + obRatePlansHeight + unitsHeight + 34 : 0);
    }
    else {
      return c.h + (this.expanded ? ratePlansHeight + obRatePlansHeight + unitsHeight : 0);
    }
  }
}

export class CalendarRatePlanAccomodationUnitTypeView implements CalendarExpandableRow {
  constructor(
    public id: number,
    public ratePlanId: number,
    public parentId: number | null,
    public parentRatePlanId: number | null,
    public name: string,
    public currencyId: number,
    public currencyCode: string,
    public isDerived: boolean,
    public isSpecialOrPackageOffer: boolean,
    public isDefault: boolean,
    public derivedFrom: string,
    public expanded: boolean,
    public height: number,
    public offset: number,
    public availability: any[],
    public minPersons: number,
    public maxPersons: number,
    public minStay: number,
    public maxStay: number,
    public groupSelected: boolean | null,
    public applyOBLOSCategories: boolean = false,
  ) { }

  availabilityOnDay(day: DateTime) {
    const ds = day.toISODate();
    // prettier-ignore
    return this.availability ? this.availability.find(a => a.date.substring(0, 10) === ds) : {};
  }

  merge(unitTypeAvailabilities: any[]) {
    this.expandLeft(unitTypeAvailabilities);
    this.availability = _.uniqBy(this.availability, x => x.date);
  }

  mergeBlockedAvailabilities(blockedAvailabilities: BlockedAvailabilityModel[]) {
    this.availability.forEach(x => x.isBlocked = blockedAvailabilities.some(e => e.date === x.date));
  }

  private expandLeft(unitTypeAvailabilities: any[]) {
    this.availability.splice(
      0,
      0,
      ...unitTypeAvailabilities
    );
  }

  h(c: CalendarBoxConfigCell, numOfRows: number) {
    return c.h + (this.expanded ? c.h * numOfRows : 0);
  }
  hi(c: CalendarBoxConfigCell, showInputPrices: boolean, numOfRows: number) {
    return (this.expanded && showInputPrices && numOfRows == 0) ? c.h : 0;
  }
  
  calculateRateBaseHeight(c: CalendarBoxConfigCell,
    showRatePlans: boolean,
    showInputPrices: boolean,
    showRatePlanFields: string[],
    showListRatePlans: number[],
  ) {
    if (!showRatePlans) return 0;
    if (!showListRatePlans.includes(this.ratePlanId)) return 0;

    const baseHeight = this.h(c, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length)
      + this.hi(c, showInputPrices, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length);

    return baseHeight;
  }

  //calculate rate height if it were to be expanded
  calculateRateExpandedHeight(c: CalendarBoxConfigCell,
    showRatePlans: boolean,
    showOBRules: boolean,
    showInputPrices: boolean,
    showRatePlanFields: string[],
    showListRatePlans: number[],
    obRules: CalendarRatePlanAccomodationUnitTypeView[]
  ) {
    if (!showRatePlans) return 0;
    if (!showListRatePlans.includes(this.ratePlanId)) return 0;
    
    const obRatePlansHeight = showOBRules && this.expanded ? obRules.reduce(
      (acc, x) => acc + x.h(c, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length)
        + x.hi(c, showInputPrices, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length),
      0
    ) : 0

    return obRatePlansHeight;
  }


  calculateObRateTotalHeight(c: CalendarBoxConfigCell,
    showRatePlans: boolean,
    showOBRules: boolean,
    showInputPrices: boolean,
    showRatePlanFields: string[],
    parentId: number
  ) {
    if (!showRatePlans) return 0;
    if (!showOBRules) return 0;
    if (parentId !== this.parentId) return 0;

    const baseHeight = this.h(c, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length)
      + this.hi(c, showInputPrices, !showInputPrices && showRatePlanFields.length > 0 && showRatePlanFields.includes("inputPrice") ? (showRatePlanFields.length - 1) : showRatePlanFields.length);

    return baseHeight;
  }
}

export class CalendarAvailabilityView {
  constructor(
    id: number,
    unitTypeId: number,
    //ratePlanId: number,
    ratePlansAccomodationUnitTypeId: number,
    date: string,
    quantity: number,
    ownerQuantity: string,
    price: number,
    currencyId: number,
    minimumStay: number,
    maximumStay: number,
    minBookableBeforeCheckin: number,
    maxBookableBeforeCheckin: number,
    noArrivals: boolean,
    noDepartures: boolean,
    noSales: boolean,
    inputPrice: number,
    maxAvailability: number,
    availabilityOffset: number
  ) {
    this.id = id;
    this.unitTypeId = unitTypeId;
    //this.ratePlanId = ratePlanId;
    this.ratePlansAccomodationUnitTypeId = ratePlansAccomodationUnitTypeId;
    this.date = date;
    this.quantity = quantity;
    this.ownerQuantity = ownerQuantity;
    this.price = price;
    this.currencyId = currencyId;
    this.minimumStay = minimumStay;
    this.maximumStay = maximumStay;
    this.minBookableBeforeCheckin = minBookableBeforeCheckin;
    this.maxBookableBeforeCheckin = maxBookableBeforeCheckin;
    this.noArrivals = noArrivals;
    this.noDepartures = noDepartures;
    this.noSales = noSales;
    this.inputPrice = inputPrice;
    this.maxAvailability = maxAvailability;
    this.availabilityOffset = availabilityOffset;
  }

  id: number;
  unitTypeId: number;
  //ratePlanId: number;
  ratePlansAccomodationUnitTypeId: number;
  date: string;
  quantity: number;
  ownerQuantity: string;
  price: number;
  currencyId: number;
  minimumStay: number;
  maximumStay: number;
  minBookableBeforeCheckin: number;
  maxBookableBeforeCheckin: number;
  noArrivals: boolean;
  noDepartures: boolean;
  noSales: boolean;
  inputPrice: number;
  maxAvailability: number;
  availabilityOffset: number;
}

export class CalendarUnitView {
  constructor(
    id: number,
    typeId: number,
    statusId: number,
    maintenanceAssignedToTenantUserId: number | null,
    name: string,
    shortName: string,
    note: string,
    height: number,
    reservations: any[]
  ) {
    this.id = id;
    this.typeId = typeId;
    this.statusId = statusId;
    this.name = name;
    this.shortName = shortName;
    this.note = note;
    this.baseHeight = height;
    this.height = height;
    this.reservations = reservations;
    this.maintenanceAssignedToTenantUserId = maintenanceAssignedToTenantUserId;
  }

  id = 0;
  typeId = 0;
  statusId = 1;
  maintenanceAssignedToTenantUserId = null as number | null;
  name = "";
  shortName = "";
  note = "";
  baseHeight = 0;
  height = 0;
  reservations?: any[] = [];
  tempReservations?: any[] = [];
  hideReservationIds: number[] = [];
  maxReservationsInDay?: number = 0;

  allReservations() {
    return this.reservations?.concat(this.tempReservations?.filter(x => !this.reservations?.some(y => y.id == x.id)) ?? []).filter(x => !this.hideReservationIds.includes(x.id));
  }

  addReservation(r: any) {
    this.tempReservations?.push(r);
    r.accomodationUnitId = this.id;
    this.calculateOverbooking();
  }

  removeReservation(r: any) {
    this.tempReservations = this.tempReservations?.filter((x) => x.id !== r.id);
    r.accomodationUnitId = 0;
    this.calculateOverbooking();
  }

  hideReservation(r: any) {
    this.hideReservationIds.push(r.id);
  }

  calculateOverbooking() {
    let maxReservationsInDay = 1;
    const allReservations = this.allReservations();

    allReservations && allReservations.forEach((x) => (x.index = 0));

    if (allReservations && allReservations.length >= 1) {
      const minDate = new Date(
        Math.min(
          ...allReservations.map((x) => new Date(x.arrivalDate).getTime())
        )
      );
      const maxDate = new Date(
        Math.max(
          ...allReservations.map((x) => new Date(x.departureDate).getTime())
        )
      );

      const currentDate = _.cloneDeep(minDate);
      while (currentDate <= maxDate) {
        let reservationsOnDay = allReservations.filter(
          (x) =>
            (new Date(x.arrivalDate).getTime() <= currentDate.getTime() &&
              new Date(x.departureDate).getTime() > currentDate.getTime()) ||
            (new Date(x.arrivalDate).getTime() == currentDate.getTime() &&
              new Date(x.departureDate).getTime() == currentDate.getTime())
        );

        const reservationUniqueIndexes = new Set();
        let reservationIndex = 0;
        const hasOverbookingOnDate = reservationsOnDay
          // filter out day stays and reservations that end on the day other reservation begins.
          .filter(
            (x) =>
              !(new Date(x.arrivalDate).getTime() == new Date(x.departureDate).getTime()) &&
              !(new Date(x.arrivalDate).getTime() != new Date(x.departureDate).getTime() &&
                new Date(x.departureDate).getTime() == currentDate.getTime())
          ).length > 1;

        reservationsOnDay = reservationsOnDay.sort((a, b) => new Date(b.arrivalDate).getTime() - new Date(a.arrivalDate).getTime());

        reservationsOnDay.sort(function (a, b) {
          if (new Date(a.departureDate).getTime() == currentDate.getTime() &&
            new Date(b.arrivalDate).getTime() == currentDate.getTime()) {
            return -1
          }
          return differenceInDays(new Date(b.arrivalDate), new Date(b.departureDate)) -
            differenceInDays(new Date(a.arrivalDate), new Date(a.departureDate))
        }
        ).forEach(
          (x) => {
            x.index = Math.max(x.index ?? 0, reservationIndex++);

            if (reservationUniqueIndexes && reservationUniqueIndexes.has(x.index)) {
              x.index++;
              reservationIndex++;
            }
            reservationUniqueIndexes.add(x.index);
            // needs to be kept as 'true' if any earlier day set it to 'true', but must be reset to exact value when checking arrival date
            x.isOverbooked = new Date(x.arrivalDate).getTime() == currentDate.getTime() ? hasOverbookingOnDate : (x.isOverbooked ? x.isOverbooked : hasOverbookingOnDate);
          }
        );
        maxReservationsInDay = Math.max(
          maxReservationsInDay,
          reservationIndex
        );

        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    this.height = this.baseHeight * maxReservationsInDay;
    this.maxReservationsInDay = maxReservationsInDay;
  }
}

export type RatePlanField = {
  key: string;
  textResource: string;
  textResourceShort: string;
  type: "number" | "boolean";
  isRestriction: boolean;
  isDerived: boolean;
};
export const ratePlanFields: RatePlanField[] = [
  {
    textResource: "RatePlan.InputPrice",
    textResourceShort: "RatePlan.InputPrice.Short",
    key: "inputPrice",
    type: "number",
    isRestriction: false,
    isDerived: false
  },
  {
    textResource: "RatePlan.MinimumStay",
    textResourceShort: "RatePlan.MinimumStay.Short",
    key: "minimumStay",
    type: "number",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.MaximumStay",
    textResourceShort: "RatePlan.MaximumStay.Short",
    key: "maximumStay",
    type: "number",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.MinBookableBeforeCheckin",
    textResourceShort: "RatePlan.MinBookableBeforeCheckin.Short",
    key: "minBookableBeforeCheckin",
    type: "number",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.MaxBookableBeforeCheckin",
    textResourceShort: "RatePlan.MaxBookableBeforeCheckin.Short",
    key: "maxBookableBeforeCheckin",
    type: "number",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.NoArrivals",
    textResourceShort: "RatePlan.NoArrivals.Short",
    key: "noArrivals",
    type: "boolean",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.NoDepartures",
    textResourceShort: "RatePlan.NoDepartures.Short",
    key: "noDepartures",
    type: "boolean",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.NoSales",
    textResourceShort: "RatePlan.NoSales.Short",
    key: "noSales",
    type: "boolean",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.MaxAvailability",
    textResourceShort: "RatePlan.MaxAvailability.Short",
    key: "maxAvailability",
    type: "number",
    isRestriction: true,
    isDerived: true
  },
  {
    textResource: "RatePlan.AvailabilityOffset",
    textResourceShort: "RatePlan.AvailabilityOffset.Short",
    key: "availabilityOffset",
    type: "number",
    isRestriction: true,
    isDerived: true
  }
];

export type RatePlan = {
  id: number;
  name: string;
  currencyId: number;
  currencyCode: string;
  isSpecialOrPackageOffer: boolean;
  isDerived: boolean;
  isOBSpecific: boolean;
};

export type MaintenanceTeamMember = {
  tenantUserId: number;
  name: string;
}

export type BulkEditResponseModel = {
  asyncBulkEditStarted: boolean;
}

export type BulkEditRatePlanModel = {
  ratePlanId: number;
  applyOBLOSCategories: boolean;
  relatedRatePlansPriceUpdateType: number;
  relatedRatePlansRestrictionsUpdateType: number;
}

export enum RelatedRatePlansPriceUpdateTypeEnum {
  ApplyOBLOSCategories = 1,
  ApplyChange = 2,
  DontUpdate = 3
}

export enum RelatedRatePlansRestrictionsUpdateTypeEnum {
  UpdateDerivedAndOBSpecific = 1,
  UpdateDerivedOnly = 2,
  UpdateOBSpecificOnly = 3,
  DontUpdate = 4
} 